import React, { memo } from 'react';
import { LucideIcon } from 'lucide-react';
import styled, { css } from 'styled-components';

export interface IQuickFilterChip {
  id: string;
  label: string;
  /** Число строк под фильтром (с сервера); `null` — ещё не знаем. */
  count?: number | null;
  /** Подсказка: что именно отбирает фильтр. */
  hint?: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

export interface IQuickFilterToggle extends IQuickFilterChip {
  pressed: boolean;
}

export interface IQuickFilterChipsProps {
  /** Подпись группы для чтеца экрана. */
  label: string;
  /** Взаимоисключающие фильтры; `allId` — «Все» (снимает выбор). */
  options: ReadonlyArray<IQuickFilterChip>;
  /** Выбранный фильтр; `null` — «Все». */
  value: string | null;
  onChange: (id: string | null) => void;
  allId?: string;
  /** Независимые переключатели («Выгорают», «Только активные»). */
  toggles?: ReadonlyArray<IQuickFilterToggle>;
  onToggle?: (id: string, pressed: boolean) => void;
  formatCount?: (count: number) => string;
}

/**
 * ТЗ 0103 — быстрые фильтры вкладки: кнопки `aria-pressed` с числами (стиль
 * `FilterChip` доски задач). Один выбор среди взаимоисключающих + свои
 * независимые переключатели. Числа приходят с сервера: фильтр одной
 * страницы на фронте врал бы.
 */
export const QuickFilterChips = memo(
  ({
    label,
    options,
    value,
    onChange,
    allId = 'all',
    toggles = [],
    onToggle,
    formatCount = String,
  }: IQuickFilterChipsProps) => {
    const face = (chip: IQuickFilterChip) => {
      const Icon = chip.icon;
      return (
        <>
          {Icon && <Icon size={12} strokeWidth={2} aria-hidden="true" />}
          {chip.label}
          {chip.count !== undefined && chip.count !== null && (
            <Count>{formatCount(chip.count)}</Count>
          )}
        </>
      );
    };
    return (
      <Bar role="group" aria-label={label}>
        {options.map((chip) => {
          const active = chip.id === allId ? value === null : value === chip.id;
          return (
            <Chip
              key={chip.id}
              type="button"
              aria-pressed={active}
              $active={active}
              title={chip.hint}
              disabled={chip.disabled}
              onClick={() => onChange(chip.id === allId ? null : chip.id)}
            >
              {face(chip)}
            </Chip>
          );
        })}
        {toggles.length > 0 && <Divider aria-hidden="true" />}
        {toggles.map((chip) => (
          <Chip
            key={chip.id}
            type="button"
            aria-pressed={chip.pressed}
            $active={chip.pressed}
            title={chip.hint}
            disabled={chip.disabled || !onToggle}
            onClick={() => onToggle?.(chip.id, !chip.pressed)}
          >
            {face(chip)}
          </Chip>
        ))}
      </Bar>
    );
  },
);

QuickFilterChips.displayName = 'QuickFilterChips';

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Chip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  padding: 4px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  ${({ $active }) =>
    $active &&
    css`
      border-color: #2563eb;
      background: #eff6ff;
      color: #1d4ed8;
    `}

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 1px;
  }

  /* Недоступный чип должен ВЫГЛЯДЕТЬ недоступным. */
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const Count = styled.span`
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

const Divider = styled.span`
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background: #e2e8f0;
`;
